import {
  IonAccordion,
  IonAccordionGroup,
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToolbar,
  RefresherEventDetail,
  useIonAlert,
  useIonViewDidEnter,
} from "@ionic/react";
import { chevronBackOutline, chevronBackSharp } from "ionicons/icons";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import { useHistory, useParams } from "react-router";
import OnlineStatus from "../components/OnlineStatus";
import {
  InspectionDef,
  PointDefType,
  PointValue,
  PointEqDescription,
} from "../models/inspections/Inspections";
// import { decodeParam } from "../util/ApiHelper";
import "./Inspection.css";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import ApiError from "../components/ApiError";
import {
  getInspectionFromLocal,
  saveInspectionToLocal,
} from "../util/InspectionsHelper";
import React from "react";
import { TestEquipment, useWorkOrderStore } from "../store/wo-order";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import useGetInspection from "../hooks/work-order/inspection/useGetInspection";
import { useShallow } from "zustand/react/shallow";
import useCompleteInspection from "../hooks/work-order/useCompleteInspection";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";
import { useOfflineStore } from "../store/offline-mode";
import useGetTestEquipmentListAndRule from "../hooks/work-order/inspection/useGetTestEquipmentListAndRule";
import { decodeParam } from "../util/ApiHelper";

const Inspection: React.FC = () => {
  interface PointError {
    id: number;
    column: string;
    error: string;
  }
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const history = useHistory();
  const { inspectionId, offlineInspectionId, workOrderId } = useParams<{
    inspectionId: string;
    offlineInspectionId: string;
    workOrderId: string;
  }>();

  const [errorState, setErrorState] = useState("");

  const [currentTestEquip, setCurrentTestEquip] = useState("[]");
  const [inspectionDefinition, setInspectionDefinition] =
    useState<InspectionDef>();
  const [showLoading, setShowLoading] = useState(false);
  const [pointDefs, setPointDefs] = useState<PointDefType[]>([]);
  const [pointVals, setPointVals] = useState<PointValue[]>([]);
  const [checkOverride, setCheckOverride] = useState<boolean>(false);
  const [disableInsp, setDisableInsp] = useState<boolean>(false);
  const [comments, setComments] = useState<string>("");
  const [pointEquipment, setPointEquipment] = useState<PointEqDescription[]>(
    []
  );
  const [testEquipmentList, setTestEquipmentList] = useState<TestEquipment[]>(
    []
  );
  const [testEquipRule, setTestEquipRule] = useState("");

  const displayWo = decodeParam(workOrderId);
  const { completeInspection } = useCompleteInspection();
  const { getTestEquipmentListAndRule } = useGetTestEquipmentListAndRule();

  const { trigger } = useGetWorkOrder();
  const { getInspection, getInspectionDefHierarchy } = useGetInspection();
  const workOrder = useWorkOrderStore((state) =>
    state.getWorkOrderById(displayWo)
  );
  const inspectionData = useWorkOrderStore(
    useShallow((state) => state.getInspectionDataForWo(displayWo))
  );

  const addInspectionToBeCompleted = useWoOfflineUpdatesStore(
    (state) => state.addInspectionToBeCompleted
  );
  const updateById = useWoOfflineUpdatesStore((state) => state.updateById);
  const updateStatusById = useWoOfflineUpdatesStore(
    (state) => state.updateStatusById
  );
  const makeOperationCompletedById = useWoOfflineUpdatesStore(
    (state) => state.makeOperationCompletedById
  );
  const isOffline = useOfflineStore((state) => state.isOffline);
  const equipment = workOrder?.Equipment ?? "";
  const [presentAlert] = useIonAlert();

  const isEditingInOffline = !!(typeof offlineInspectionId === "string"
    ? offlineInspectionId.trim()
    : "");

  const requiredColor = "danger";
  let inRefresh = false;
  const [completedAlertShown, setCompletedAlertShown] = useState(false);
  const pointValMap = React.useMemo(() => {
    const map = new Map<string, PointValue>();
    pointVals.forEach((pv) => {
      map.set(`${pv.PointId}_${pv.ColumnCode}`, pv);
    });
    return map;
  }, [pointVals]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    inRefresh = true;
    if (!disableInsp) {
      await getInspectionDefHierarchy(inspectionId, displayWo);
    }
    event.detail.complete();
    inRefresh = false;
  };

  const processInspecError = useCallback(
    (status: number, errorData: any) => {
      let errMsg = "";
      console.log("Inspection Original Error Message:", errorData);
      if (errorData) {
        if (status === 400 && typeof errorData === "object") {
          errMsg = errorData.Message ?? "";
        } else if (status === 500 && Array.isArray(errorData)) {
          errMsg = errorData[0]?.Text ?? JSON.stringify(errorData);
        } else {
          errMsg =
            typeof errorData === "string"
              ? errorData
              : JSON.stringify(errorData);
        }
        console.log("Inspection Processed Error Message:", errMsg);

        if (
          typeof errMsg === "string" &&
          errMsg.toLowerCase().includes("relogin")
        ) {
          console.log(
            "Inspection Error Message includes relogin:",
            errMsg.toLowerCase()
          );

          errMsg =
            translatedMessages["SessionExpired"]?.MessageText ||
            "Your session has expired. Please re-login.";
        }

        setErrorState(errMsg);
      }

      return errMsg;
    },
    [translatedMessages]
  );

  const getWoEquipment = useCallback(async () => {
    if (workOrder) return; //got it from the cache
    await trigger(displayWo);
  }, [displayWo, trigger, workOrder]);

  const getTestEquipmentList = useCallback(async () => {
    if (!testEquipmentList || testEquipRule === undefined) {
      const cachedTestEquipmentList =
        useWorkOrderStore.getState().testEquipmentList;

      const cachedTestEquipRule = useWorkOrderStore.getState().testEquipRule;
      if (cachedTestEquipmentList && cachedTestEquipRule) {
        setTestEquipRule(cachedTestEquipRule);
        setTestEquipmentList(cachedTestEquipmentList);
        return;
      }
      const { equipList, equipRule } = await getTestEquipmentListAndRule();
      setTestEquipRule(equipRule);
      setTestEquipmentList(equipList);
    }
  }, [getTestEquipmentListAndRule, testEquipRule, testEquipmentList]);

  const loadExistingResult = useCallback(async () => {
    const inspectionResult = inspectionData?.inspectionResults;

    if (inspectionResult?.InspectionResult) {
      let itemDesc: { [key: string]: string } = {};
      itemDesc = inspectionResult.PointEqpList;
      const pVals: PointValue[] = [];
      const pDefs: PointDefType[] = [];
      const equipmentVal: Set<string> = new Set();
      const equipmentValues: PointEqDescription[] = [];
      !!inspectionResult.InspectionResult.Definition &&
        inspectionResult.InspectionResult.Definition.Points.forEach(
          (p: PointDefType) => {
            if (p.Equipment != null) {
              const equipDescription = itemDesc[p.Equipment];
              if (!equipmentVal.has(p.Equipment)) {
                const eq: PointEqDescription = {
                  Equipment: p.Equipment,
                  Description: equipDescription || "",
                };
                equipmentValues.push(eq);
                equipmentVal.add(p.Equipment);
                pDefs.push(p);
              }
              //add value of p.equipment and equipment description into an object of pointeQDescription and add to set
            } else if (p.Equipment === null) {
              // FIX: create a copy before modifying
              const pCopy = withNoEquipment(p);
              if (!equipmentVal.has("No Equipment")) {
                const eq: PointEqDescription = {
                  Equipment: pCopy.Equipment,
                  Description: "",
                };
                equipmentValues.push(eq);
              }
              equipmentVal.add(pCopy.Equipment);
              pDefs.push(pCopy);
            }
          }
        );

      setPointEquipment(Array.from(equipmentValues));

      !!inspectionResult.InspectionResult.Points &&
        inspectionResult.InspectionResult.Points.forEach((p: PointValue) => {
          pVals.push({
            PointId: p.PointId,
            ObjectId: p.ObjectId.replace(
              "|" + (inspectionData?.inspectionId ?? inspectionId),
              ""
            ),
            ColumnCode: p.ColumnCode,
            Measure: p.Measure,
            FailedFlag: p.FailedFlag,
          });
        });
      setComments(inspectionResult.InspectionResult?.Comments);
      setCurrentTestEquip(
        JSON.stringify(inspectionResult.InspectionResult.TestEquipment)
      );
      setPointDefs(pDefs);
      setPointVals(pVals);
      setInspectionDefinition(inspectionResult.InspectionResult.Definition);
    }
  }, [
    inspectionData?.inspectionId,
    inspectionData?.inspectionResults,
    inspectionId,
  ]);

  const getInspectionDef = useCallback(async () => {
    const inspectionDef = inspectionData?.inspectionDef;

    if (inspectionDef?.InspectionDefinition) {
      const pDefs: PointDefType[] = [];
      const pVals: PointValue[] = [];
      const equipmentVal: Set<string> = new Set();
      const equipmentValues: PointEqDescription[] = [];
      let itemDesc: { [key: string]: string } = {};
      itemDesc = inspectionDef.PointEqpList;
      //parse points metadata
      [...inspectionDef.InspectionDefinition.Points].forEach(
        (p1: PointDefType) => {
          const p = { ...p1 };
          if (p.ValueDataType === "b") {
            p.DefaultValue = "false";
          }
          if (p.RequiredFlag) {
            p.Description = "* " + p.Description;
          }
          let highLow: { high: number; low: number } = {
            high: Number.MAX_VALUE,
            low: Number.MIN_VALUE,
          };
          let desired =
            p.Meta?.find((m) => {
              return m.MetaType === "desired";
            })?.MetaValue ?? 0;
          !!p.Meta &&
            p.Meta.forEach((m) => {
              if (m.MetaType === "limit") {
                highLow.high =
                  m.MetaSubtype === "high" ? m.MetaValue : highLow.high;
                highLow.low =
                  m.MetaSubtype === "low" ? m.MetaValue : highLow.low;
                p.Limit = true;
              } else if (m.MetaType === "tolerance") {
                highLow.high =
                  m.MetaSubtype === "high" ? m.MetaValue : highLow.high;
                highLow.low =
                  m.MetaSubtype === "low" ? m.MetaValue : highLow.low;
                p.Tolerance = true;
              } else if (m.MetaType === "tolerance%") {
                highLow.high =
                  m.MetaSubtype === "high"
                    ? (1 + m.MetaValue * 0.01) * desired
                    : highLow.high;
                highLow.low =
                  m.MetaSubtype === "low"
                    ? (1 - Math.abs(m.MetaValue) * 0.01) * desired
                    : highLow.low;
                p.TolerancePercent = true;
              } else if (m.MetaType === "precision") {
                p.Precision = m.MetaValue;
              } else if (m.MetaType === "default") {
                p.DefaultValue = m.MetaSubtype;
              }
            });
          p.HighValue = highLow.high;
          p.LowValue = highLow.low;

          if (p.Equipment != null) {
            const equipDescription = itemDesc[p.Equipment];
            if (!equipmentVal.has(p.Equipment)) {
              const eq: PointEqDescription = {
                Equipment: p.Equipment,
                Description: equipDescription || "",
              };
              equipmentValues.push(eq);
              equipmentVal.add(p.Equipment);
              pDefs.push(p);
            }
            //add value of p.equipment and equipment description into an object of pointeQDescription and add to set
          } else if (p.Equipment === null) {
            // FIX: create a copy before modifying
            const pCopy = withNoEquipment(p);
            if (!equipmentVal.has("No Equipment")) {
              const eq: PointEqDescription = {
                Equipment: pCopy.Equipment,
                Description: "",
              };
              equipmentValues.push(eq);
            }
            equipmentVal.add(pCopy.Equipment);
            pDefs.push(pCopy);
          }
          !!p.Columns &&
            p.Columns.forEach((pc) => {
              pVals.push({
                PointId: p.PointId,
                ObjectId: pc.ObjectId,
                ColumnCode: pc.ColumnCode,
                Measure: p.DefaultValue,
                FailedFlag: false,
              });
            });
        }
      );
      setPointEquipment(Array.from(equipmentValues));
      if (inspectionDef.InspectionDefinition.InspectionType === "Calibration") {
        getTestEquipmentList();
      }
      setPointDefs(pDefs);
      setPointVals(pVals);
      setInspectionDefinition(inspectionDef.InspectionDefinition);
    }
  }, [getTestEquipmentList, inspectionData?.inspectionDef]);

  const formatErrorMessage = (error: string | string[]): string => {
    if (Array.isArray(error)) {
      return error.join("\n"); // Use newlines for better readability
    }
    return error;
  };

  useEffect(() => {
    const errors: PointError[] = [];
    pointVals.forEach((pv) => {
      const def = pointDefs.find((pd) => pd.PointId === pv.PointId);
      if (def?.RequiredFlag && (pv.Measure == null || pv.Measure === "")) {
        errors.push({
          id: pv.PointId,
          column: pv.ObjectId,
          error:
            def.Description +
            "->" +
            (translations["lbl_value_required"] || "Value is Required."),
        });
      }
    });
    setValidationErrors(errors);
  }, [pointVals, pointDefs, translations]);

  useIonViewDidEnter(() => {
    if (isOffline && !isEditingInOffline) {
      const addedForCompletion = useWoOfflineUpdatesStore
        .getState()
        .hasInspectionAddedForCompletionInOffline(inspectionId, displayWo);
      if (addedForCompletion) {
        presentAlert({
          message:
            translatedMessages["MobOfflineInspAddedForComp"]?.MessageText ||
            "Inspection already added in offline store for completion.",
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
              handler: () => {
                history.goBack();
              },
            },
          ],
        });
      }
    }
  }, [isOffline, isEditingInOffline, inspectionId, displayWo]);

  const checkExistingResult = useCallback(async () => {
    const inspectionFromLocal = await getInspectionFromLocal(
      inspectionId,
      displayWo
    );
    if (inspectionFromLocal) {
      const pVals: PointValue[] = [];
      const pDefs: PointDefType[] = [];
      const equipmentVal: Set<string> = new Set();
      const equipmentValues: PointEqDescription[] = [];
      let itemDesc: { [key: string]: string } = {};
      !!inspectionFromLocal.InspectionResultData.Definition &&
        inspectionFromLocal.InspectionResultData.Definition.Points.forEach(
          (p: PointDefType) => {
            if (p.Equipment != null) {
              const equipDescription = itemDesc[p.Equipment];
              if (!equipmentVal.has(p.Equipment)) {
                const eq: PointEqDescription = {
                  Equipment: p.Equipment,
                  Description: equipDescription || "",
                };
                equipmentValues.push(eq);
                equipmentVal.add(p.Equipment);
              }
              pDefs.push(p);
              //add value of p.equipment and equipment description into an object of pointeQDescription and add to set
            } else if (p.Equipment === null) {
              // FIX: create a copy before modifying
              const pCopy = withNoEquipment(p);
              if (!equipmentVal.has("No Equipment")) {
                const eq: PointEqDescription = {
                  Equipment: pCopy.Equipment,
                  Description: "",
                };
                equipmentValues.push(eq);
              }
              equipmentVal.add(pCopy.Equipment);
              pDefs.push(pCopy);
            } else {
              pDefs.push(p);
            }
          }
        );
      setPointEquipment(Array.from(equipmentValues));

      !!inspectionFromLocal.InspectionResultData.Points &&
        inspectionFromLocal.InspectionResultData.Points.forEach(
          (p: PointValue) => {
            pVals.push({
              PointId: p.PointId,
              ObjectId: p.ObjectId,
              ColumnCode: p.ColumnCode,
              Measure: p.Measure,
              FailedFlag: p.FailedFlag,
            });
          }
        );
      if (
        inspectionFromLocal?.InspectionResultData?.Definition
          ?.InspectionType === "Calibration"
      ) {
        getTestEquipmentList();
        setCurrentTestEquip(
          JSON.stringify(inspectionFromLocal.InspectionResultData.TestEquipment)
        );
      }
      setComments(inspectionFromLocal.InspectionResultData.Comments);
      setPointDefs(pDefs);
      setPointVals(pVals);
      setInspectionDefinition(
        inspectionFromLocal.InspectionResultData.Definition
      );
    } else if (offlineInspectionId) {
      const offlineInspection = useWoOfflineUpdatesStore
        .getState()
        .getInspectionById(offlineInspectionId);
      if (offlineInspection) {
        const pVals: PointValue[] = [];
        const pDefs: PointDefType[] = [];
        const equipmentVal: Set<string> = new Set();
        const equipmentValues: PointEqDescription[] = [];
        let itemDesc: { [key: string]: string } = {};
        !!offlineInspection.inspectionDefinition &&
          offlineInspection.inspectionDefinition.Points.forEach(
            (p: PointDefType) => {
              pDefs.push(p);
              if (p.Equipment != null) {
                const equipDescription = itemDesc[p.Equipment];
                if (!equipmentVal.has(p.Equipment)) {
                  const eq: PointEqDescription = {
                    Equipment: p.Equipment,
                    Description: equipDescription || "",
                  };
                  equipmentValues.push(eq);
                  equipmentVal.add(p.Equipment);
                }
                //add value of p.equipment and equipment description into an object of pointeQDescription and add to set
              } else if (p.Equipment === null) {
                // FIX: create a copy before modifying
                const pCopy = withNoEquipment(p);
                if (!equipmentVal.has("No Equipment")) {
                  const eq: PointEqDescription = {
                    Equipment: pCopy.Equipment,
                    Description: "",
                  };
                  equipmentValues.push(eq);
                }
                equipmentVal.add(pCopy.Equipment);
              } else {
                pDefs.push(p);
              }
            }
          );
        setPointEquipment(Array.from(equipmentValues));

        !!offlineInspection.pointVals &&
          offlineInspection.pointVals.forEach((p: PointValue) => {
            pVals.push({
              PointId: p.PointId,
              ObjectId: p.ObjectId,
              ColumnCode: p.ColumnCode,
              Measure: p.Measure,
              FailedFlag: p.FailedFlag,
            });
          });
        if (
          offlineInspection.inspectionDefinition.InspectionType ===
          "Calibration"
        ) {
          getTestEquipmentList();
          setCurrentTestEquip(offlineInspection.currentTestEquip);
        }
        setComments(offlineInspection.comments);
        setPointDefs(pDefs);
        setPointVals(pVals);
        setInspectionDefinition(offlineInspection.inspectionDefinition);
      }
    } else if (inspectionData?.inspectionResults) {
      loadExistingResult();

      if (inspectionData?.isCompleted && !completedAlertShown) {
        setDisableInsp(true);
        setCompletedAlertShown(true);
        presentAlert({
          header: "Completed",
          message:
            translatedMessages["CompInspExist"]?.MessageText ||
            "Completed inspection already exists for this work order.",
          buttons: [translations["lbl_btn_ok"] || "OK"],
        });
      }
    } else {
      await getInspectionDef();
    }
  }, [
    completedAlertShown,
    displayWo,
    getInspectionDef,
    getTestEquipmentList,
    inspectionData?.inspectionResults,
    inspectionData?.isCompleted,
    inspectionId,
    loadExistingResult,
    offlineInspectionId,
    presentAlert,
    translatedMessages,
    translations,
  ]);

  useEffect(() => {
    (async () => {
      setShowLoading(true);
      await getWoEquipment();
      if (!inspectionData) {
        const { data, hasError, status } = await getInspection(displayWo);
        if (hasError) processInspecError(status, data);
        else await checkExistingResult();
      } else {
        await checkExistingResult();
      }
      setShowLoading(false);
    })();
  }, [
    checkExistingResult,
    displayWo,
    getInspection,
    getWoEquipment,
    inspectionData,
    inspectionId,
    processInspecError,
    workOrderId,
  ]);

  useEffect(() => {
    setCompletedAlertShown(false);
  }, [displayWo]);

  const getMetaNotes = (meta: any) => {
    return (
      <IonNote class="inspection-helper-note" slot="helper">
        {getMetaString(meta)}
      </IonNote>
    );
  };

  const getMetaString = (meta: any) => {
    let notes: string = "";
    meta.forEach((metaValue: any) => {
      notes += `${metaValue.MetaSubtype}${
        metaValue.MetaType === "default" ? "" : ":" + metaValue.MetaValue
      }${metaValue.MetaType === "tolerance%" ? "% " : " "} `;
    });
    return notes;
  };

  const [validationErrors, setValidationErrors] = useState<PointError[]>([]);

  async function validateInput(
    inValue: any,
    pointDef: PointDefType,
    col1: any
  ) {
    const col = { ...col1 };
    let pv: PointValue = {
      PointId: pointDef.PointId,
      ObjectId: col.ObjectId,
      ColumnCode: col.ColumnCode,
      Measure: inValue,
      FailedFlag: false,
    };
    let errors = validationErrors.filter((e) => e.column !== col.ObjectId);
    if (!!pointDef && pointDef.RequiredFlag && !inValue) {
      errors.push({
        id: pointDef.PointId,
        column: col.ObjectId,
        error:
          pointDef.Description + "->" + translations["lbl_value_required"] ||
          "Value is Required.",
      });
    } else if (
      (col.ImpliesFailureFlag ||
        pointDef.Limit ||
        pointDef.Tolerance ||
        pointDef.TolerancePercent) &&
      !!pointDef &&
      !!inValue &&
      !!pointDef.Meta &&
      pointDef.ValueDataType === "n" &&
      !isNaN(+inValue)
    ) {
      //high/low
      if (!!pointDef.Precision) {
        pv.Measure = Number.parseFloat(inValue).toFixed(pointDef.Precision);
      }
      if (+inValue > pointDef.HighValue || +inValue < pointDef.LowValue) {
        pv.FailedFlag = true;
        errors.push({
          id: pointDef.PointId,
          column: col.ObjectId,
          error: pointDef.Description + "->" + getMetaString(pointDef.Meta),
        });
      }
    }
    //precision
    if (
      !col.ImpliesFailureFlag &&
      !!pointDef &&
      !!inValue &&
      pointDef.ValueDataType === "n" &&
      !isNaN(+inValue)
    ) {
      if (pointDef.Precision === 0) {
        pv.Measure = Math.round(Number.parseFloat(inValue)).toString();
      } else {
        pv.Measure = Number.parseFloat(inValue).toFixed(pointDef.Precision);
      }
    }
    col.Failed = pv.FailedFlag;
    setPointVals([
      ...pointVals.filter(
        (p) => !(p.PointId === col.PointId && p.ColumnCode === col.ColumnCode)
      ),
      pv,
    ]);
    setValidationErrors(errors);
  }

  const parseTestEquipment = (testEquipString: string): TestEquipment[] => {
    try {
      return JSON.parse(testEquipString || "[]");
    } catch (e) {
      console.error("Failed to parse currentTestEquip JSON:", e);
      return [];
    }
  };

  async function validateList(inValue: any, pointDef: PointDefType, col1: any) {
    const col = { ...col1 };
    let pv: PointValue = {
      PointId: pointDef.PointId,
      ObjectId: col.ObjectId,
      ColumnCode: col.ColumnCode,
      Measure: inValue,
      FailedFlag: false,
    };
    let errors = validationErrors.filter((e) => e.column !== col.ObjectId);
    if (!!pointDef && pointDef.RequiredFlag && !inValue) {
      errors.push({
        id: pointDef.PointId,
        column: col.ObjectId,
        error:
          pointDef.Description + "->" + translations["lbl_value_required"] ||
          "Value is Required.",
      });
    } else if (
      col.ImpliesFailureFlag &&
      !!pointDef &&
      !!inValue &&
      !!pointDef.Meta
    ) {
      let metaVal = pointDef.Meta.find(
        (m) => m.MetaType === "valid_value" && m.MetaSubtype === inValue
      );
      if (metaVal?.MetaValue === 1) {
        pv.FailedFlag = true;
        errors.push({
          id: pointDef.PointId,
          column: col.ObjectId,
          error: pointDef.Description + "->" + metaVal.MetaSubtype,
        });
      }
    }
    col.Failed = pv.FailedFlag;
    setPointVals([
      ...pointVals.filter(
        (p) => !(p.PointId === col.PointId && p.ColumnCode === col.ColumnCode)
      ),
      pv,
    ]);
    setValidationErrors(errors);
  }

  async function setCheckBoxValue(
    inValue: boolean,
    pointDef: PointDefType,
    col: any
  ) {
    let pv: PointValue = {
      PointId: pointDef.PointId,
      ObjectId: col.ObjectId,
      ColumnCode: col.ColumnCode,
      Measure: String(inValue),
      FailedFlag: pointDef.RequiredFlag ? !inValue : false,
    };
    setPointVals([
      ...pointVals.filter(
        (p) => !(p.PointId === col.PointId && p.ColumnCode === col.ColumnCode)
      ),
      pv,
    ]);
  }

  async function completeInspectionResults() {
    const comments = (
      document.getElementById("txtComments") as HTMLInputElement
    ).value;
    const data = {
      inspectionId,
      pointVals,
      inspectionDefinition: inspectionDefinition!,
      checkOverride,
      comments,
      currentTestEquip,
      woNumber: displayWo,
    };
    if (isOffline) {
      console.log(
        `inspection data for offline with id '${offlineInspectionId}' & isEditing - ${isEditingInOffline}`
      );

      isEditingInOffline
        ? updateById(offlineInspectionId, data)
        : addInspectionToBeCompleted(data);
      presentAlert({
        message:
          translatedMessages["MobOfflineInspComp"]?.MessageText ||
          "Inspection added in offline store for completion.",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            handler: () => {
              history.goBack();
            },
          },
        ],
      });
      return; //we will add it once user comes online
    }

    setShowLoading(true);
    const response = await completeInspection(data);
    if (response.isError) {
      const msg = processInspecError(response.status, response.data);
      setErrorState(formatErrorMessage(msg));
      if (offlineInspectionId)
        updateStatusById(offlineInspectionId, "failed", [msg]);
    } else {
      if (offlineInspectionId) makeOperationCompletedById(offlineInspectionId);
      //process respose data?
      presentAlert({
        message:
          translatedMessages["CompInsp"]?.MessageText ||
          "Inspection completed successfully",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            handler: () => {
              const setWoInspectionData =
                useWorkOrderStore.getState().setWoInspectionData;
              setWoInspectionData(displayWo, {
                isCompleted: true,
                inspectionResults: {
                  InspectionResult: {
                    Definition: inspectionDefinition,
                    Points: pointVals,
                    Comments: comments,
                    TestEquipment: parseTestEquipment(currentTestEquip),
                  },
                },
                inspectionId: inspectionId,
              });
              history.goBack();
            },
          },
        ],
      });
      setErrorState("");
    }
    setShowLoading(false);
  }

  async function saveInspectionResultsToLocal() {
    setShowLoading(true);
    const comments = (
      document.getElementById("txtComments") as HTMLInputElement
    ).value;
    const res = await saveInspectionToLocal(
      displayWo,
      equipment,
      inspectionId,
      pointVals,
      inspectionDefinition!,
      checkOverride,
      comments,
      currentTestEquip
    );
    setShowLoading(false);
    if (res) {
      presentAlert({
        header: "Success",
        message: "Inspection successfully saved!",
        buttons: [translations["lbl_btn_ok"] || "OK"],
      });
    } else {
      presentAlert({
        header: "Error",
        message: "Inspection was not saved",
        buttons: [translations["lbl_btn_ok"] || "OK"],
      });
    }
  }

  // Helper function to copy a point and set Equipment to "No Equipment"
  function withNoEquipment(p: PointDefType): PointDefType {
    return { ...p, Equipment: "No Equipment" };
  }

  function safeJsonParse<T>(jsonString: string | null | undefined): T | null {
    try {
      if (!jsonString) return null;
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              icon={{ ios: chevronBackOutline, md: chevronBackSharp }}
              text=""
              defaultHref={`/workorder/${workOrderId}`}
            />
          </IonButtons>
          <IonTitle>
            {translations["lbl_category_inspection"] || "Inspections"}
          </IonTitle>
        </IonToolbar>
        <OnlineStatus />
        <ApiError errorData={errorState} />
      </IonHeader>

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <IonLoading
          isOpen={showLoading && !inRefresh}
          onDidDismiss={() => setShowLoading(false)}
        />
        {
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{displayWo}</IonCardTitle>
              <IonCardSubtitle>{`${
                inspectionDefinition?.InspectionType ?? ""
              } - ${inspectionDefinition?.Description ?? ""} -${
                inspectionDefinition?.InspectionId ?? ""
              }`}</IonCardSubtitle>
              <IonCardSubtitle>{`${
                translations["lbl_category_equipment"] || "Equipment"
              }: ${equipment}`}</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonAccordionGroup>
                <IonAccordion
                  disabled={inspectionDefinition?.Instructions == null}
                  value="instructions"
                >
                  <IonItem slot="header">
                    <IonLabel>
                      {translations["lbl_category_instructions"] ||
                        "Instructions"}
                    </IonLabel>
                  </IonItem>
                  <div className="ion-padding" slot="content">
                    <ReactQuill
                      readOnly
                      value={inspectionDefinition?.Instructions}
                      modules={{ toolbar: [[]] }}
                    />
                  </div>
                </IonAccordion>
                <IonAccordion
                  disabled={
                    inspectionDefinition?.MaterialRequirements == null ||
                    inspectionDefinition?.MaterialRequirements === "NULL"
                  }
                  value="material"
                >
                  <IonItem slot="header">
                    <IonLabel>
                      {translations["lbl_material"] || "Material"}
                    </IonLabel>
                  </IonItem>
                  <div className="ion-padding" slot="content">
                    <ReactQuill
                      readOnly
                      value={inspectionDefinition?.MaterialRequirements}
                      modules={{ toolbar: [[]] }}
                    />
                  </div>
                </IonAccordion>
              </IonAccordionGroup>
            </IonCardContent>
          </IonCard>
        }
        {!!inspectionDefinition?.Elements && (
          <IonCard>
            {inspectionDefinition.Elements.map(function (element: any) {
              return (
                <IonItem key={`${element.FieldLabel}`}>
                  <IonTextarea
                    autoGrow
                    readonly
                    value={`${element.FieldLabel}: ${element.FieldValue}`}
                  ></IonTextarea>
                </IonItem>
              );
            })}
          </IonCard>
        )}
        {inspectionDefinition?.InspectionType === "Calibration" && (
          <IonCard>
            <IonList>
              <IonItem>
                <IonLabel
                  color={testEquipRule ? requiredColor : undefined}
                  position="floating"
                >
                  Testing Equipment
                </IonLabel>
                <IonSelect
                  interface="popover"
                  disabled={disableInsp}
                  placeholder="Select testing equipment(s)"
                  multiple={true}
                  selectedText={(() => {
                    const parsed =
                      safeJsonParse<TestEquipment[]>(currentTestEquip);
                    return parsed && parsed.length > 0
                      ? parsed.reduce((a, c) => a + c.Equipment + " ", "")
                      : "";
                  })()}
                  onIonChange={(ev) => {
                    setCurrentTestEquip(JSON.stringify(ev.detail.value));
                  }}
                >
                  {(testEquipmentList ?? []).map((equip) => (
                    <IonSelectOption key={equip.Equipment} value={equip}>
                      {equip.Equipment +
                        " (Exp:" +
                        new Date(
                          equip.CalibrationExpiresDate
                        ).toLocaleDateString() +
                        ")"}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>
          </IonCard>
        )}
        <IonCard>
          <IonCardContent class="inspection-ioncard-content">
            {pointEquipment.length === 1 &&
            pointEquipment[0].Equipment === "No Equipment" ? (
              <IonGrid>
                {(pointDefs ?? [])
                  .slice()
                  ?.sort((a, b) => {
                    return a.RowOrder - b.RowOrder;
                  })
                  .map(function (p, i) {
                    return (
                      <Fragment>
                        <IonRow key={`${i}.1`}>
                          <IonCol key={`${i}.1`}>{p.Category}</IonCol>
                        </IonRow>
                        <IonRow key={`${i}.2`}>
                          <IonCol
                            class="inspection-gridcol-header"
                            key={`${i}.2`}
                          >
                            {p.Label}
                          </IonCol>
                          {(p.Columns ?? [])
                            .slice()
                            ?.sort((a, b) => {
                              return a.ColumnOrder - b.ColumnOrder;
                            })
                            .map(function (col) {
                              return (
                                <IonCol
                                  class="inspection-gridcol-header-input"
                                  key={col.ObjectId}
                                >
                                  {col.ColumnCode === "_Result"
                                    ? "-"
                                    : col.ColumnCode}
                                </IonCol>
                              );
                            })}
                        </IonRow>
                        <IonRow key={`${i}.3`}>
                          <IonCol key={`${i}.3.1`}>
                            <IonLabel
                              color={p.RequiredFlag ? requiredColor : undefined}
                            >
                              {p.Description}
                            </IonLabel>
                          </IonCol>
                          {(p.Columns ?? [])
                            .slice()
                            ?.sort((a, b) => {
                              return a.ColumnOrder - b.ColumnOrder;
                            })
                            .map(function (col, j) {
                              return (
                                <IonCol key={`${col.ObjectId}.${j}`}>
                                  {p.ValueDataType === "b" ? (
                                    <IonItem
                                      color={
                                        p.RequiredFlag
                                          ? requiredColor
                                          : undefined
                                      }
                                    >
                                      <IonCheckbox
                                        slot="end"
                                        name={col.ObjectId}
                                        disabled={disableInsp}
                                        checked={
                                          pointVals.find(
                                            (p) =>
                                              p.PointId === col.PointId &&
                                              p.ColumnCode === col.ColumnCode
                                          )?.Measure === "true"
                                        }
                                        onIonChange={(ev) =>
                                          setCheckBoxValue(
                                            ev.target.checked,
                                            p,
                                            col
                                          )
                                        }
                                      ></IonCheckbox>
                                    </IonItem>
                                  ) : p.ValueDataType === "l" ? (
                                    <IonItem>
                                      <IonSelect
                                        name={col.ObjectId}
                                        class={
                                          pointValMap.get(
                                            `${col.PointId}_${col.ColumnCode}`
                                          )?.FailedFlag === true
                                            ? "inspection-ion-select-fail"
                                            : "inspection-ion-select"
                                        }
                                        interface="popover"
                                        disabled={disableInsp}
                                        placeholder={
                                          translations["lbl_select list"] ||
                                          "select from list"
                                        }
                                        value={
                                          pointVals.find(
                                            (p) =>
                                              p.PointId === col.PointId &&
                                              p.ColumnCode === col.ColumnCode
                                          )?.Measure
                                        }
                                        onIonChange={(ev) =>
                                          validateList(ev.detail.value, p, col)
                                        }
                                      >
                                        {!!p.Meta &&
                                          p.Meta.map(function (mv, index) {
                                            return (
                                              <IonSelectOption key={index}>
                                                {mv.MetaSubtype}
                                              </IonSelectOption>
                                            );
                                          })}
                                      </IonSelect>
                                    </IonItem>
                                  ) : (
                                    <IonItem>
                                      <IonInput
                                        class={
                                          pointValMap.get(
                                            `${col.PointId}_${col.ColumnCode}`
                                          )?.FailedFlag === true
                                            ? "inspection-gridcol-inputcol-fail"
                                            : "inspection-gridcol-inputcol"
                                        }
                                        name={col.ObjectId}
                                        disabled={disableInsp}
                                        required={p.RequiredFlag}
                                        type={
                                          p.ValueDataType === "n"
                                            ? "number"
                                            : "text"
                                        }
                                        inputMode={
                                          p.ValueDataType === "n"
                                            ? "decimal"
                                            : "text"
                                        }
                                        placeholder={p.DefaultValue}
                                        min={
                                          p.ValueDataType === "n"
                                            ? 0
                                            : undefined
                                        }
                                        max={
                                          p.ValueDataType === "n"
                                            ? p.HighValue
                                            : undefined
                                        }
                                        value={
                                          pointVals.find(
                                            (p) =>
                                              p.PointId === col.PointId &&
                                              p.ColumnCode === col.ColumnCode
                                          )?.Measure
                                        }
                                        onIonBlur={(ev) =>
                                          validateInput(ev.target.value, p, col)
                                        }
                                      ></IonInput>
                                      {p.ValueDataType !== "l" &&
                                        !!p.Meta &&
                                        getMetaNotes(p.Meta)}
                                    </IonItem>
                                  )}
                                </IonCol>
                              );
                            })}
                        </IonRow>
                      </Fragment>
                    );
                  })}
              </IonGrid>
            ) : (
              <IonAccordionGroup>
                {pointEquipment.map((PointEqDescription, index) => (
                  <IonAccordion
                    key={`${index}${PointEqDescription.Equipment}`}
                    value={PointEqDescription.Equipment}
                  >
                    <IonItem slot="header">
                      <IonLabel>
                        {PointEqDescription.Equipment === "No Equipment"
                          ? translations["lbl_no_equipment"] || "No Equipment"
                          : PointEqDescription.Equipment +
                            " - " +
                            PointEqDescription.Description}
                      </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                      <IonGrid>
                        {pointDefs
                          ?.filter(
                            (p) => p.Equipment === PointEqDescription.Equipment
                          )
                          .slice()
                          .sort((a, b) => {
                            return a.RowOrder - b.RowOrder;
                          })
                          .map(function (p, i) {
                            return (
                              <>
                                <IonRow key={`${i}.1`}>
                                  <IonCol key={`${i}.1`}>{p.Category}</IonCol>
                                </IonRow>
                                <IonRow key={`${i}.2`}>
                                  <IonCol
                                    class="inspection-gridcol-header"
                                    key={`${i}.2`}
                                  >
                                    {p.Label}
                                  </IonCol>
                                  {(p.Columns ?? [])
                                    .slice()
                                    ?.sort((a, b) => {
                                      return a.ColumnOrder - b.ColumnOrder;
                                    })
                                    .map(function (col) {
                                      return (
                                        <IonCol
                                          class="inspection-gridcol-header-input"
                                          key={col.ObjectId}
                                        >
                                          {col.ColumnCode === "_Result"
                                            ? "-"
                                            : col.ColumnCode}
                                        </IonCol>
                                      );
                                    })}
                                </IonRow>
                                <IonRow key={`${i}.3`}>
                                  <IonCol key={`${i}.3.1`}>
                                    <IonLabel
                                      color={
                                        p.RequiredFlag
                                          ? requiredColor
                                          : undefined
                                      }
                                    >
                                      {p.Description}
                                    </IonLabel>
                                  </IonCol>
                                  {(p.Columns ?? [])
                                    .slice()
                                    ?.sort((a, b) => {
                                      return a.ColumnOrder - b.ColumnOrder;
                                    })
                                    .map(function (col, j) {
                                      return (
                                        <IonCol key={`${col.ObjectId}.${j}`}>
                                          {p.ValueDataType === "b" ? (
                                            <IonItem
                                              color={
                                                p.RequiredFlag
                                                  ? requiredColor
                                                  : undefined
                                              }
                                            >
                                              <IonCheckbox
                                                slot="end"
                                                name={col.ObjectId}
                                                disabled={disableInsp}
                                                checked={
                                                  pointVals.find(
                                                    (p) =>
                                                      p.PointId ===
                                                        col.PointId &&
                                                      p.ColumnCode ===
                                                        col.ColumnCode
                                                  )?.Measure === "true"
                                                }
                                                onIonChange={(ev) =>
                                                  setCheckBoxValue(
                                                    ev.target.checked,
                                                    p,
                                                    col
                                                  )
                                                }
                                              ></IonCheckbox>
                                            </IonItem>
                                          ) : p.ValueDataType === "l" ? (
                                            <IonItem>
                                              <IonSelect
                                                name={col.ObjectId}
                                                class={
                                                  pointValMap.get(
                                                    `${col.PointId}_${col.ColumnCode}`
                                                  )?.FailedFlag === true
                                                    ? "inspection-ion-select-fail"
                                                    : "inspection-ion-select"
                                                }
                                                interface="popover"
                                                disabled={disableInsp}
                                                placeholder={
                                                  translations[
                                                    "lbl_select list"
                                                  ] || "select from list"
                                                }
                                                value={
                                                  pointVals.find(
                                                    (p) =>
                                                      p.PointId ===
                                                        col.PointId &&
                                                      p.ColumnCode ===
                                                        col.ColumnCode
                                                  )?.Measure
                                                }
                                                onIonChange={(ev) =>
                                                  validateList(
                                                    ev.detail.value,
                                                    p,
                                                    col
                                                  )
                                                }
                                              >
                                                {!!p.Meta &&
                                                  p.Meta.map(function (
                                                    mv,
                                                    index
                                                  ) {
                                                    return (
                                                      <IonSelectOption
                                                        key={`${index}${mv.MetaSubtype}`}
                                                      >
                                                        {mv.MetaSubtype}
                                                      </IonSelectOption>
                                                    );
                                                  })}
                                              </IonSelect>
                                            </IonItem>
                                          ) : (
                                            <IonItem>
                                              <IonInput
                                                class={
                                                  pointValMap.get(
                                                    `${col.PointId}_${col.ColumnCode}`
                                                  )?.FailedFlag === true
                                                    ? "inspection-gridcol-inputcol-fail"
                                                    : "inspection-gridcol-inputcol"
                                                }
                                                name={col.ObjectId}
                                                disabled={disableInsp}
                                                required={p.RequiredFlag}
                                                type={
                                                  p.ValueDataType === "n"
                                                    ? "number"
                                                    : "text"
                                                }
                                                inputMode={
                                                  p.ValueDataType === "n"
                                                    ? "decimal"
                                                    : "text"
                                                }
                                                placeholder={p.DefaultValue}
                                                min={
                                                  p.ValueDataType === "n"
                                                    ? 0
                                                    : undefined
                                                }
                                                max={
                                                  p.ValueDataType === "n"
                                                    ? p.HighValue
                                                    : undefined
                                                }
                                                value={
                                                  pointVals.find(
                                                    (p) =>
                                                      p.PointId ===
                                                        col.PointId &&
                                                      p.ColumnCode ===
                                                        col.ColumnCode
                                                  )?.Measure
                                                }
                                                onIonBlur={(ev) =>
                                                  validateInput(
                                                    ev.target.value,
                                                    p,
                                                    col
                                                  )
                                                }
                                              ></IonInput>
                                              {p.ValueDataType !== "l" &&
                                                !!p.Meta &&
                                                getMetaNotes(p.Meta)}
                                            </IonItem>
                                          )}
                                        </IonCol>
                                      );
                                    })}
                                </IonRow>
                              </>
                            );
                          })}
                      </IonGrid>
                    </div>
                  </IonAccordion>
                ))}
              </IonAccordionGroup>
            )}
          </IonCardContent>
        </IonCard>
        <IonCard>
          <IonCardContent class="inspection-ioncard-content">
            <IonItem fill="outline" hidden={true} color="danger">
              <IonTextarea
                class="inspection-check-label"
                color="danger"
                readonly
                autoGrow
                value={validationErrors.map((p) => p.error).join("\r\n")}
              ></IonTextarea>
            </IonItem>
            <IonItem disabled={disableInsp} fill="outline">
              <IonTextarea
                class="inspection-check-label"
                readonly
                autoGrow
                value={
                  translatedMessages["ICFailureFlagMessage"]?.MessageText ||
                  "This Inspection could not be completed within specifications."
                }
              ></IonTextarea>
              <IonCheckbox
                slot="start"
                onIonChange={(ev) => setCheckOverride(ev.target.checked)}
              ></IonCheckbox>
            </IonItem>
            <IonItem disabled={disableInsp} fill="outline">
              <IonLabel position="floating">
                {translations["lbl_category_comments"] || "Comments"}
              </IonLabel>
              <IonTextarea
                id="txtComments"
                placeholder={
                  translations["lbl_enter comments"] || "Enter Comments"
                }
                autoGrow={true}
                value={comments}
                onIonChange={(ev) => setComments(String(ev.detail.value))}
              ></IonTextarea>
            </IonItem>
          </IonCardContent>
        </IonCard>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              color="primary"
              fill="solid"
              disabled={
                disableInsp ||
                (pointVals.find((p) => p.FailedFlag) != null &&
                  !checkOverride) ||
                (!!testEquipRule && currentTestEquip === "[]") ||
                validationErrors.find((e) =>
                  e.error.toLowerCase().includes("value is required")
                ) != null
              }
              onClick={() => completeInspectionResults()}
            >
              {translations["lbl_btn_complete"] || "Complete"}
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              color="primary"
              fill="solid"
              disabled={disableInsp}
              onClick={saveInspectionResultsToLocal}
            >
              {translations["lbl_btn_save"] || "Save"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default Inspection;
